import type { GridPosition } from './types'

/** Logical point in cell units. (0, 0) is the top-left corner of cell (0, 0). */
export interface LogicalPoint {
  x: number
  y: number
}

/**
 * Render policy floor for compact devices (CSS/canvas px).
 * Touch targets for 2×2 towers are ~2× this; placement uses preview+confirm.
 * Kept low enough that a 24×18 map can nearly fit a ~390px portrait width.
 */
export const DEFAULT_MIN_PLAYABLE_CELL = 18

/** Movement above this (px) counts as camera pan, not a tap. */
export const POINTER_DRAG_THRESHOLD = 10

/**
 * OVERVIEW — shrink to show the whole map (portrait-friendly).
 * FOCUS — keep a comfortable cell size; camera pans when the map overflows.
 */
export type CameraMode = 'OVERVIEW' | 'FOCUS'

/** Geometry thresholds for choosing OVERVIEW vs FOCUS. Tunable in one place. */
export const VIEWPORT_POLICY = {
  /**
   * If complete-fit coverage of the viewport falls below this, prefer FOCUS
   * (unless the viewport itself is portrait — see portraitAspectMin).
   */
  minOverviewCoverage: 0.52,
  /** height/width at or above this → treat viewport as portrait. */
  portraitAspectMin: 1.15,
  /** In FOCUS, aim to fill this fraction of available width with the map. */
  focusWidthFill: 0.9,
  /** Prefer at least this many map rows visible in FOCUS. */
  focusMinVisibleRows: 14,
  /** Clamp FOCUS cell size so zoom stays readable, not absurd. */
  minFocusCellSize: 24,
  maxFocusCellSize: 72,
  padding: 2 as number
} as const

/**
 * Current render transform for one map inside the Phaser canvas.
 * scroll* pans the world; offset* centers when the map is smaller than the viewport.
 */
export interface GridView {
  cellSize: number
  mapWidth: number
  mapHeight: number
  viewportWidth: number
  viewportHeight: number
  mapPixelWidth: number
  mapPixelHeight: number
  offsetX: number
  offsetY: number
  scrollX: number
  scrollY: number
  maxScrollX: number
  maxScrollY: number
  cameraMode: CameraMode
}

export interface ComputeGridViewOptions {
  padding?: number
  minCellSize?: number
  /** When true, never enlarge past fit — show the whole map (may use small cells). */
  preferCompleteFit?: boolean
  /** Force camera mode; otherwise resolved from geometry. */
  cameraMode?: CameraMode
}

export interface ViewportDecision {
  mode: CameraMode
  view: GridView
  /** Complete-fit map area / viewport area (0–1). */
  coverage: number
  fitCellSize: number
}

export function cellCenterLogical(x: number, y: number): LogicalPoint {
  return { x: x + 0.5, y: y + 0.5 }
}

/** Portrait/taller maps prefer fitting the full board; wide maps keep a playable floor. */
export function defaultMinCellForMap(mapWidth: number, mapHeight: number): number {
  return mapHeight > mapWidth ? 1 : DEFAULT_MIN_PLAYABLE_CELL
}

export function isPortraitViewport(
  availableWidth: number,
  availableHeight: number,
  portraitAspectMin = VIEWPORT_POLICY.portraitAspectMin
): boolean {
  return availableHeight / Math.max(1, availableWidth) >= portraitAspectMin
}

/** How much of the viewport a complete-fit render would occupy. */
export function fitCoverageRatio(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  padding = VIEWPORT_POLICY.padding
): { coverage: number; fitCellSize: number } {
  const viewportWidth = Math.max(1, availableWidth)
  const viewportHeight = Math.max(1, availableHeight)
  const innerWidth = Math.max(1, viewportWidth - padding * 2)
  const innerHeight = Math.max(1, viewportHeight - padding * 2)
  const fitCellSize = Math.max(
    1,
    Math.floor(Math.min(innerWidth / mapWidth, innerHeight / mapHeight))
  )
  const mapArea = mapWidth * fitCellSize * (mapHeight * fitCellSize)
  const coverage = mapArea / (viewportWidth * viewportHeight)
  return { coverage, fitCellSize }
}

/**
 * Decide OVERVIEW vs FOCUS from map + viewport geometry (not user-agent).
 *
 * Portrait viewports → OVERVIEW (phones keep the full board readable).
 * Landscape/desktop with low complete-fit coverage → FOCUS (avoid skinny columns).
 */
export function chooseCameraMode(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  padding = VIEWPORT_POLICY.padding
): CameraMode {
  if (isPortraitViewport(availableWidth, availableHeight)) return 'OVERVIEW'

  const { coverage } = fitCoverageRatio(
    mapWidth,
    mapHeight,
    availableWidth,
    availableHeight,
    padding
  )
  if (coverage >= VIEWPORT_POLICY.minOverviewCoverage) return 'OVERVIEW'
  return 'FOCUS'
}

/** Comfortable square cell for FOCUS: fills most width, keeps useful vertical context. */
export function focusCellSize(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  padding = VIEWPORT_POLICY.padding
): number {
  const { fitCellSize } = fitCoverageRatio(
    mapWidth,
    mapHeight,
    availableWidth,
    availableHeight,
    padding
  )
  const innerWidth = Math.max(1, availableWidth - padding * 2)
  const innerHeight = Math.max(1, availableHeight - padding * 2)
  const byWidth = Math.floor((innerWidth * VIEWPORT_POLICY.focusWidthFill) / mapWidth)
  const byRows = Math.floor(innerHeight / VIEWPORT_POLICY.focusMinVisibleRows)
  const candidate = Math.min(
    VIEWPORT_POLICY.maxFocusCellSize,
    Math.max(VIEWPORT_POLICY.minFocusCellSize, Math.min(byWidth, byRows))
  )
  // Never smaller than complete-fit (FOCUS only enlarges).
  return Math.max(fitCellSize, candidate)
}

function buildGridView(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  cellSize: number,
  cameraMode: CameraMode
): GridView {
  const viewportWidth = Math.max(1, availableWidth)
  const viewportHeight = Math.max(1, availableHeight)
  const mapPixelWidth = mapWidth * cellSize
  const mapPixelHeight = mapHeight * cellSize
  const maxScrollX = Math.max(0, mapPixelWidth - viewportWidth)
  const maxScrollY = Math.max(0, mapPixelHeight - viewportHeight)
  const offsetX = maxScrollX === 0 ? Math.floor((viewportWidth - mapPixelWidth) / 2) : 0
  const offsetY = maxScrollY === 0 ? Math.floor((viewportHeight - mapPixelHeight) / 2) : 0

  return {
    cellSize,
    mapWidth,
    mapHeight,
    viewportWidth,
    viewportHeight,
    mapPixelWidth,
    mapPixelHeight,
    offsetX,
    offsetY,
    scrollX: 0,
    scrollY: 0,
    maxScrollX,
    maxScrollY,
    cameraMode
  }
}

/**
 * Resolve mode + GridView for the current shell slot.
 * Prefer this over calling computeGridView with ad-hoc preferCompleteFit flags.
 */
export function resolveViewportPolicy(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  options: ComputeGridViewOptions = {}
): ViewportDecision {
  const padding = options.padding ?? VIEWPORT_POLICY.padding
  const { coverage, fitCellSize } = fitCoverageRatio(
    mapWidth,
    mapHeight,
    availableWidth,
    availableHeight,
    padding
  )
  const mode =
    options.cameraMode ??
    (options.preferCompleteFit === true
      ? 'OVERVIEW'
      : options.preferCompleteFit === false
        ? 'FOCUS'
        : chooseCameraMode(mapWidth, mapHeight, availableWidth, availableHeight, padding))

  const cellSize =
    mode === 'OVERVIEW'
      ? fitCellSize
      : options.minCellSize !== undefined
        ? Math.max(fitCellSize, options.minCellSize)
        : focusCellSize(mapWidth, mapHeight, availableWidth, availableHeight, padding)

  return {
    mode,
    coverage,
    fitCellSize,
    view: buildGridView(mapWidth, mapHeight, availableWidth, availableHeight, cellSize, mode)
  }
}

/**
 * Choose a playable cell size, then either fit+center the map or leave room for a camera.
 * desiredCellSize = max(fitCellSize, minPlayableCellSize) unless preferCompleteFit / OVERVIEW.
 */
export function computeGridView(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  options: number | ComputeGridViewOptions = {}
): GridView {
  const opts: ComputeGridViewOptions =
    typeof options === 'number' ? { padding: options } : options

  // Legacy path: explicit preferCompleteFit / minCellSize without going through policy.
  if (
    opts.cameraMode === undefined &&
    (opts.preferCompleteFit !== undefined || opts.minCellSize !== undefined)
  ) {
    const padding = opts.padding ?? VIEWPORT_POLICY.padding
    const preferCompleteFit =
      opts.preferCompleteFit ?? (mapHeight > mapWidth)
    const minCellSize =
      opts.minCellSize ?? (preferCompleteFit ? 1 : DEFAULT_MIN_PLAYABLE_CELL)
    const { fitCellSize } = fitCoverageRatio(
      mapWidth,
      mapHeight,
      availableWidth,
      availableHeight,
      padding
    )
    const cellSize = preferCompleteFit ? fitCellSize : Math.max(fitCellSize, minCellSize)
    const mode: CameraMode =
      preferCompleteFit || (cellSize === fitCellSize && fitCellSize >= minCellSize)
        ? 'OVERVIEW'
        : 'FOCUS'
    // If we enlarged past fit, it is FOCUS.
    const resolvedMode: CameraMode = cellSize > fitCellSize ? 'FOCUS' : mode
    return buildGridView(
      mapWidth,
      mapHeight,
      availableWidth,
      availableHeight,
      cellSize,
      resolvedMode
    )
  }

  return resolveViewportPolicy(
    mapWidth,
    mapHeight,
    availableWidth,
    availableHeight,
    opts
  ).view
}

export function clampScroll(
  view: GridView,
  scrollX: number,
  scrollY: number
): { scrollX: number; scrollY: number } {
  return {
    scrollX: Math.min(view.maxScrollX, Math.max(0, scrollX)),
    scrollY: Math.min(view.maxScrollY, Math.max(0, scrollY))
  }
}

export function withScroll(view: GridView, scrollX: number, scrollY: number): GridView {
  const clamped = clampScroll(view, scrollX, scrollY)
  return { ...view, scrollX: clamped.scrollX, scrollY: clamped.scrollY }
}

/** Place the focus point near the upper-left of the viewport (spawn-friendly). */
export function scrollToShowLogical(
  view: GridView,
  focus: LogicalPoint,
  biasX = 0.22,
  biasY = 0.22
): GridView {
  const worldX = focus.x * view.cellSize
  const worldY = focus.y * view.cellSize
  return withScroll(
    view,
    worldX - view.viewportWidth * biasX,
    worldY - view.viewportHeight * biasY
  )
}

/**
 * Frame a logical point with comfortable context (spawn / first path).
 * Reusable later for jump-to-tower / jump-to-enemy without auto-chasing.
 * Wide desktop slots use a more centered bias so tall maps are not stuck left.
 */
export function focusAroundLogical(
  view: GridView,
  focus: LogicalPoint,
  biasX?: number,
  biasY?: number
): GridView {
  const wide = view.viewportWidth >= 900
  const bx = biasX ?? (wide ? 0.45 : 0.28)
  const by = biasY ?? (wide ? 0.35 : 0.22)
  return scrollToShowLogical(view, focus, bx, by)
}

/** Keep the same logical viewport center when cell size / canvas size changes. */
export function preserveViewportFocus(previous: GridView, next: GridView): GridView {
  const center = logicalFromScreen(
    previous,
    previous.viewportWidth / 2,
    previous.viewportHeight / 2
  )
  return scrollToShowLogical(next, center, 0.5, 0.5)
}

/** World pixels relative to the map origin (for objects inside a camera container). */
export function worldFromLogical(
  view: Pick<GridView, 'cellSize'>,
  point: LogicalPoint
): { x: number; y: number } {
  return {
    x: point.x * view.cellSize,
    y: point.y * view.cellSize
  }
}

export function screenFromLogical(view: GridView, point: LogicalPoint): { x: number; y: number } {
  return {
    x: view.offsetX - view.scrollX + point.x * view.cellSize,
    y: view.offsetY - view.scrollY + point.y * view.cellSize
  }
}

export function logicalFromScreen(view: GridView, screenX: number, screenY: number): LogicalPoint {
  return {
    x: (screenX - view.offsetX + view.scrollX) / view.cellSize,
    y: (screenY - view.offsetY + view.scrollY) / view.cellSize
  }
}

export function gridFromLogical(point: LogicalPoint): GridPosition {
  return {
    x: Math.floor(point.x),
    y: Math.floor(point.y)
  }
}

export function screenFromGridCell(
  view: GridView,
  x: number,
  y: number
): { x: number; y: number } {
  return screenFromLogical(view, cellCenterLogical(x, y))
}

export function screenToGrid(view: GridView, screenX: number, screenY: number): GridPosition {
  return gridFromLogical(logicalFromScreen(view, screenX, screenY))
}

export function gridToScreen(view: GridView, x: number, y: number): { x: number; y: number } {
  return screenFromGridCell(view, x, y)
}

export function cameraNeedsPan(view: GridView): boolean {
  return view.maxScrollX > 0 || view.maxScrollY > 0
}

/**
 * Size the game canvas for the shell slot.
 * OVERVIEW may tighten height to the fitted map (avoid a tall empty Phaser box).
 * FOCUS keeps the full slot height so the camera can pan a larger cell grid.
 */
export function preferredCanvasSize(
  mapWidth: number,
  mapHeight: number,
  availableWidth: number,
  availableHeight: number,
  options: ComputeGridViewOptions = {}
): { width: number; height: number; cellSize: number; cameraMode: CameraMode } {
  const width = Math.max(1, availableWidth)
  const height = Math.max(1, availableHeight)
  const decision = resolveViewportPolicy(mapWidth, mapHeight, width, height, options)

  if (decision.mode === 'FOCUS') {
    return {
      width,
      height,
      cellSize: decision.view.cellSize,
      cameraMode: 'FOCUS'
    }
  }

  const padding = options.padding ?? VIEWPORT_POLICY.padding
  const tightHeight = Math.min(height, decision.view.mapPixelHeight + padding * 2)
  const tight = resolveViewportPolicy(mapWidth, mapHeight, width, tightHeight, {
    ...options,
    cameraMode: 'OVERVIEW'
  })
  return {
    width,
    height: tightHeight,
    cellSize: tight.view.cellSize,
    cameraMode: 'OVERVIEW'
  }
}
